<script lang="ts" setup>
import { ChartOverlay } from '@forgd/ui-kit/charts/chart-overlay'
import { ChartTitle } from '@forgd/ui-kit/charts/chart-title'
import { chartColors } from '@forgd/ui-kit/charts/lib/colors'
import { getBarWidth } from '#core/services/unovis/helpers'
import { calculateChartHeight } from '#public-research/utils/pr.utils'
import { formatValue } from '@forgd/formatter'
import { ColorKey, ColorKeyLabel, ColorKeyValue } from '@forgd/ui-kit/color-key'
import { VisAxis, VisStackedBar, VisXYContainer } from '@unovis/vue'
import { useElementSize } from '@vueuse/core'

type ListingsByExchangeResponse = SuccessResponseBody<'/public-research/listings-by-exchange'>
type ListingByDateType = ListingsByExchangeResponse['byMonth'][number]

const props = defineProps<{
  byMonth: ListingByDateType[]
}>()

const MAX_TICKS_TO_DISPLAY = 48

const legend: { label: string, color: string, key: keyof ListingByDateType }[] = [
  {
    label: 'Primary (< 24 hours)',
    color: chartColors.tokenDesigner[0],
    key: 'primaryCount',
  },
  {
    label: 'Follow-on (> 24 hours)',
    color: chartColors.tokenDesigner[1],
    key: 'followOnCount',
  },
]

const chartData = computed(() =>
  (props.byMonth ?? []).map((item, index) => ({
    ...item,
    index,
  })),
)

type ChartDataItem = ListingByDateType & { index: number }

function xTickFormat(v: number) {
  const data = chartData.value[v]
  return formatValue(data?.month, 'date:month')
}
const MIN_PX_PER_TICK = 30

const xTicks = computed(() => {
  const data = chartData.value
  if (!data?.length) {
    return []
  }

  const width = containerWidth.value
  const maxTicks = width > 0
    ? Math.max(1, Math.floor(width / MIN_PX_PER_TICK))
    : MAX_TICKS_TO_DISPLAY
  const step = Math.max(1, Math.ceil(data.length / maxTicks))

  return data
    .filter((_, i) => i % step === 0)
    .map(item => item.index)
})

const xAccessor = (d: ChartDataItem) => d.index

const yAccessors = computed(() => [
  (d: ChartDataItem) => d.primaryCount,
  (d: ChartDataItem) => d.followOnCount,
])
const colors = computed(() => [
  chartColors.tokenDesigner[0],
  chartColors.tokenDesigner[1],
])

const chartContainer = useTemplateRef('chartContainer')
const { width: containerWidth } = useElementSize(chartContainer)

const legendContainer = useTemplateRef('legendContainer')
const { height: legendHeight } = useElementSize(legendContainer)

const chartHeight = computed(() => calculateChartHeight({ legendHeight: legendHeight.value || 44, padding: 40 }))

const yDomain = computed(() => {
  if (chartData.value.length === 0) {
    return [0, 100]
  }
  const maxValue = Math.max(
    ...chartData.value.map(item => item.followOnCount + item.primaryCount),
  )
  return [0, maxValue * 1.05]
})

const barWidth = computed(() => {
  const gap = chartData.value.length > MAX_TICKS_TO_DISPLAY ? 4 : 10
  return getBarWidth(chartData.value, { width: containerWidth.value, gap }, 40)
})
</script>

<template>
  <div ref="chartContainer" class="flex h-full flex-col" :style="{ '--crosshair-bottom-offset': '29px' }">
    <ChartTitle text="Number of Listings | Date" />
    <PrChartPlaceholder :height="chartHeight">
      <VisXYContainer :data="chartData" :y-domain="yDomain" :height="chartHeight">
        <VisStackedBar
          :x="xAccessor"
          :y="yAccessors"
          :color="colors"
          :bar-width="barWidth"
        />
        <VisAxis
          type="x"
          :tick-format="xTickFormat"
          :tick-values="xTicks"
          :tick-line="false"
          :grid-line="false"
        />
        <VisAxis
          type="y"
          :num-ticks="7"
          :tick-line="false"
          :grid-line="true"
        />
        <ChartOverlay>
          <template #default="{ d }">
            <div class="flex min-w-50 flex-col gap-2">
              <div class="text-xs font-semibold">
                {{ formatValue(chartData[d.index!]?.month, 'date:month') }}
              </div>
              <ColorKey v-for="item in legend" :key="item.label" :color="item.color" layout="justify">
                <ColorKeyLabel>{{ item.label }}</ColorKeyLabel>
                <ColorKeyValue>{{ formatValue(d[item.key], 'number:0') }}</ColorKeyValue>
              </ColorKey>
            </div>
          </template>
        </ChartOverlay>
      </VisXYContainer>
    </PrChartPlaceholder>
    <div ref="legendContainer" class="mt-5 flex shrink-0 gap-2">
      <ColorKey v-for="item in legend" :key="item.label" :color="item.color" layout="inline" class="shrink-0">
        <ColorKeyLabel>{{ item.label }}</ColorKeyLabel>
      </ColorKey>
    </div>
  </div>
</template>
